import Seo from '@/components/Seo';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { getBooks } from '@/lib/api';

const BlogPage = () => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const books = await getBooks();
        const flat = books.flatMap((b) =>
          (b.reviews || []).map((r) => ({ ...r, bookTitle: b.title, bookId: b.slug || b.id }))
        );
        setReviews(flat);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="blog-page">
      <Seo title="Reader Reviews" description="What readers are saying about The Omission of Billy Seldom, Shadows in the Magnolias, and other novels by Kavi Ocktiiv." />
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="overline text-gold mb-4">Reader Reviews</p>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Reviews
          </h1>
          <p className="text-white/60 max-w-2xl">
            What readers are saying about the novels of Kavi Ocktiiv. Every review reflects a real reader's
            journey through the pages of Ink and Innovation's titles.
          </p>
        </motion.div>

        {/* Reviews List */}
        {isLoading ? (
          <div className="space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card-surface p-8">
                <div className="h-4 bg-white/5 w-1/4 mb-4 img-loading" />
                <div className="h-8 bg-white/5 w-3/4 mb-4 img-loading" />
                <div className="h-16 bg-white/5 img-loading" />
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-24" data-testid="reviews-empty">
            <p className="text-white/40 text-lg">No reviews yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {reviews.map((review, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="card-surface p-8 lg:p-10"
                data-testid={`review-card-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < review.rating ? 'text-gold fill-gold' : 'text-white/20'}
                    />
                  ))}
                </div>

                <h2
                  className="text-xl lg:text-2xl font-medium mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {review.title}
                </h2>

                <p className="text-white/60 leading-relaxed italic mb-6">"{review.text}"</p>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="text-white/70">{review.reviewer}</span>
                  {review.location && (
                    <span className="text-white/40" data-testid={`review-location-${index}`}>{review.location}</span>
                  )}
                  <span className="text-white/30">·</span>
                  <Link
                    to={`/books/${review.bookId}`}
                    className="text-gold/80 hover:text-gold transition-colors"
                    data-testid={`review-book-link-${index}`}
                  >
                    {review.bookTitle}
                  </Link>
                  {review.format && (
                    <>
                      <span className="text-white/30">·</span>
                      <span className="text-white/40">{review.format}</span>
                    </>
                  )}
                  {review.date && (
                    <>
                      <span className="text-white/30">·</span>
                      <span className="text-white/40">{review.date}</span>
                    </>
                  )}
                  {review.verified && (
                    <span className="text-gold/70">✓ {review.verified_label || 'Verified Purchase'}</span>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
